import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ApiService from '../../../_services/ApiService';
import ConfirmModal from './ConfirmModal';
import PromptModal from './PromptModal';

// Types kept loose — server response is the source of truth.
interface ChangeRow {
  id: number;
  entityType: string;
  entityId: number;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  beforeJson: any;
  afterJson: any;
  changedByUserId: number;
  changedByUserName: string | null;
  changedAt: string;
  changeBatchId: string | null;
  source: string | null;
  revertedAt: string | null;
}

interface SnapshotRow {
  id: number;
  name: string | null;
  description: string | null;
  snapshotType: string;
  createdAt: string;
  createdByUserId: number;
  createdByUserName: string | null;
}

interface VersionRow {
  id: number;
  versionNumber: number;
  label: string | null;
  description: string | null;
  snapshotId: number;
  isActive: number;
  createdByUserId: number;
  createdByUserName: string | null;
  createdAt: string;
}

interface DiffSlot {
  id: number;
  name: string | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  changedFields?: string[];
  before?: Record<string, any>;
  after?: Record<string, any>;
}

interface SnapshotDiff {
  templateChanged: boolean;
  templateBefore?: Record<string, any>;
  templateAfter?: Record<string, any>;
  templateChangedFields?: string[];
  slotsAdded: DiffSlot[];
  slotsRemoved: DiffSlot[];
  slotsChanged: DiffSlot[];
}

interface Props {
  templateId: number;
  /** Called after any successful rollback action so the parent can refetch. */
  onChange: () => void;
}

type Tab = 'changes' | 'snapshots' | 'versions';

// Map of internal "source" tags → user-facing action label.
const SOURCE_LABELS: Record<string, string> = {
  add_slot: 'Added slot',
  update_slot: 'Edited slot',
  update_slot_apply_to_future: 'Edited slot (applied to future)',
  delete_slot: 'Deleted slot',
  bulk_delete_slots: 'Bulk-deleted slots',
  confirm_slot: 'Confirmed slot',
  unconfirm_slot: 'Revoked confirmation on slot',
  publish_template: 'Published timetable',
  unpublish_template: 'Unpublished timetable',
  update_template: 'Edited timetable details',
  update_template_batches: 'Updated associated batches',
  slot_designer_create: 'Added slot via Slot Designer',
  slot_designer_update: 'Edited slot via Slot Designer',
  slot_designer_delete: 'Deleted slot via Slot Designer',
  swap_full: 'Swapped two slots (full)',
  swap_content: 'Swapped two slots (content)',
  bulk_import: 'Imported slots from Excel',
  bulk_import_replace_wipe: 'Imported slots from Excel',
  snapshot_restore: 'Restored a snapshot',
};

// Field-name → friendly label (for "Edited slot: time, instructor")
const FIELD_LABELS: Record<string, string> = {
  date: 'date',
  startTime: 'start time',
  endTime: 'end time',
  dayOfWeekId: 'day',
  instructorId: 'instructor',
  topicMasterId: 'topic',
  roomId: 'room',
  agenda: 'agenda',
  name: 'name',
  remarks: 'remarks',
  publishStatus: 'publish state',
  startDate: 'start date',
  endDate: 'end date',
  confirmedByUserId: 'confirmation',
  confirmedAt: 'confirmation',
};

const HIDDEN_FIELDS = new Set([
  'id',
  'lastModifiedDate',
  'lastModifiedByUserId',
  'statusChangedOn',
  'statusChangedByUserId',
  'publishedAt', // covered by publishStatus
  'publishedByUserId',
]);

const formatSlotContext = (row: ChangeRow): string => {
  // Pull date + start/end time from after JSON (or before, for deletes).
  const j = row.afterJson || row.beforeJson || {};
  const date = j.date ? String(j.date).substring(0, 10) : null;
  const start = j.startTime ? String(j.startTime).substring(0, 5) : null;
  const end = j.endTime ? String(j.endTime).substring(0, 5) : null;
  if (!date) return '';
  if (start && end) return ` — ${date} ${start}–${end}`;
  return ` — ${date}`;
};

const changedFieldLabels = (row: ChangeRow): string[] => {
  const before = row.beforeJson || {};
  const after = row.afterJson || {};
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const k of Object.keys(after)) {
    if (HIDDEN_FIELDS.has(k)) continue;
    if (String(before[k] ?? '') === String(after[k] ?? '')) continue;
    const label = FIELD_LABELS[k] || k;
    if (seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels;
};

const summarize = (row: ChangeRow): string => {
  // First priority: source-based human label.
  if (row.source && SOURCE_LABELS[row.source]) {
    let s = SOURCE_LABELS[row.source];
    if (row.entityType === 'activityslot_template') s += formatSlotContext(row);
    return s;
  }

  // Reverts get their own label
  if (row.source?.startsWith('revert_of_')) return 'Reverted earlier change';

  // Fallback: friendly operation + entity
  const entityLabel =
    row.entityType === 'activityslot_template'
      ? 'slot'
      : row.entityType === 'timetable_template'
        ? 'timetable details'
        : row.entityType === 'slot_batch_mapping'
          ? 'slot–batch link'
          : row.entityType === 'timetable_batch_mapping'
            ? 'batch association'
            : row.entityType.replace(/_/g, ' ');

  if (row.operation === 'INSERT') return `Added ${entityLabel}${formatSlotContext(row)}`;
  if (row.operation === 'DELETE') return `Removed ${entityLabel}${formatSlotContext(row)}`;

  // UPDATE without a recognised source: show which fields changed in plain language.
  const changed = changedFieldLabels(row);
  if (changed.length === 0) return `Edited ${entityLabel}${formatSlotContext(row)}`;
  const list = changed.slice(0, 3).join(', ') + (changed.length > 3 ? `, +${changed.length - 3} more` : '');
  return `Edited ${entityLabel}: ${list}${formatSlotContext(row)}`;
};

const formatTimestamp = (s: string) => {
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
};

const RollbackPanel: React.FC<Props> = ({ templateId, onChange }) => {
  const [tab, setTab] = useState<Tab>('changes');
  const [changes, setChanges] = useState<ChangeRow[]>([]);
  const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [diff, setDiff] = useState<SnapshotDiff | null>(null);
  const [diffFrom, setDiffFrom] = useState<number | null>(null);
  const [diffTo, setDiffTo] = useState<number | null>(null);

  // Help modal — explains the difference between the three tabs.
  const [helpOpen, setHelpOpen] = useState(false);

  // Page-styled dialogs replace native window.confirm / window.prompt.
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    message: string;
    confirmText?: string;
    onConfirm: () => void;
  } | null>(null);
  const [promptDialog, setPromptDialog] = useState<{
    title: string;
    label?: string;
    placeholder?: string;
    confirmText?: string;
    onSubmit: (value: string) => void;
  } | null>(null);

  const loadChanges = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ApiService.getRecentChanges(templateId, { limit: 100, onlyActive: false });
      if (r.data?.success) setChanges(r.data.data || []);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  const loadSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ApiService.listSnapshots(templateId);
      if (r.data?.success) setSnapshots(r.data.data || []);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ApiService.listVersions(templateId);
      if (r.data?.success) {
        const rows: VersionRow[] = r.data.data || [];
        setVersions(rows);
        if (rows.length >= 2 && diffFrom === null && diffTo === null) {
          setDiffFrom(rows[rows.length - 2].id);
          setDiffTo(rows[rows.length - 1].id);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [templateId, diffFrom, diffTo]);

  // On first mount (or when templateId changes), load all three lists so the
  // tab counts are correct from the start — not just when the user clicks each
  // tab. After that, subsequent tab clicks refresh only the active one.
  useEffect(() => {
    loadChanges();
    loadSnapshots();
    loadVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  useEffect(() => {
    if (tab === 'changes') loadChanges();
    if (tab === 'snapshots') loadSnapshots();
    if (tab === 'versions') loadVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Browser undo: Cmd/Ctrl+Z reverts the most recent non-reverted change.
  // Mod+Shift+Z un-reverts (the revert itself is a change too).
  useEffect(() => {
    const onKey = async (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.key.toLowerCase() !== 'z') return;
      // Avoid trampling text-field undo
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      e.preventDefault();
      try {
        const r = await ApiService.getRecentChanges(templateId, { limit: 1, onlyActive: true });
        const top: ChangeRow | undefined = r.data?.data?.[0];
        if (!top) return;
        await ApiService.revertChange(top.id);
        onChange();
        if (tab === 'changes') loadChanges();
      } catch {
        // swallow — user can fall back to the panel
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [templateId, onChange, tab, loadChanges]);

  const handleRevert = (changeId: number) => {
    setConfirmDialog({
      title: 'Revert change',
      message: 'Revert this change? You can revert the revert if you change your mind.',
      confirmText: 'Revert',
      onConfirm: async () => {
        setConfirmDialog(null);
        await ApiService.revertChange(changeId);
        onChange();
        loadChanges();
      },
    });
  };

  const handleRevertBatch = (batchId: string) => {
    setConfirmDialog({
      title: 'Revert batch',
      message: 'Revert every change in this group? They will all be reversed together.',
      confirmText: 'Revert batch',
      onConfirm: async () => {
        setConfirmDialog(null);
        await ApiService.revertChangeBatch(batchId);
        onChange();
        loadChanges();
      },
    });
  };

  const handleCreateSnapshot = () => {
    setPromptDialog({
      title: 'Save snapshot',
      label: 'Snapshot name (optional)',
      placeholder: 'e.g. Before Term 2 changes',
      confirmText: 'Save snapshot',
      onSubmit: async (name: string) => {
        setPromptDialog(null);
        await ApiService.createSnapshot(templateId, name.trim() ? { name: name.trim() } : {});
        loadSnapshots();
      },
    });
  };

  const handleRestoreSnapshot = (snapshotId: number) => {
    setConfirmDialog({
      title: 'Restore snapshot',
      message:
        'Restore this snapshot? The current state will be saved as an automatic snapshot first, so this can be reversed.',
      confirmText: 'Restore',
      onConfirm: async () => {
        setConfirmDialog(null);
        await ApiService.restoreSnapshot(templateId, snapshotId);
        onChange();
        loadSnapshots();
      },
    });
  };

  const handleCreateVersion = () => {
    setPromptDialog({
      title: 'Create version',
      label: 'Version label (optional)',
      placeholder: 'e.g. Reviewed by Programme Head',
      confirmText: 'Create version',
      onSubmit: async (label: string) => {
        setPromptDialog(null);
        await ApiService.createVersion(templateId, label.trim() ? { label: label.trim() } : {});
        loadVersions();
      },
    });
  };

  const handleRestoreVersion = (versionId: number) => {
    setConfirmDialog({
      title: 'Restore version',
      message:
        'Restore this version? The current state will be auto-snapshotted first, so this can be reversed.',
      confirmText: 'Restore',
      onConfirm: async () => {
        setConfirmDialog(null);
        await ApiService.restoreVersion(templateId, versionId);
        onChange();
        loadVersions();
      },
    });
  };

  const handleRunDiff = async () => {
    if (!diffFrom || !diffTo) return;
    const r = await ApiService.diffVersions(templateId, diffFrom, diffTo);
    if (r.data?.success) setDiff(r.data.data);
  };

  // Group recent-changes by batch to avoid wall-of-rows for bulk operations
  const groupedChanges = useMemo(() => {
    const map = new Map<string, ChangeRow[]>();
    const order: string[] = [];
    for (const c of changes) {
      const key = c.changeBatchId || `solo:${c.id}`;
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(c);
    }
    return order.map((k) => ({ key: k, items: map.get(k)! }));
  }, [changes]);

  return (
    <div className="rollback-panel">
      <div className="rollback-tabs">
        <button className={tab === 'changes' ? 'active' : ''} onClick={() => setTab('changes')}>
          Recent Changes ({changes.length})
        </button>
        <button className={tab === 'snapshots' ? 'active' : ''} onClick={() => setTab('snapshots')}>
          Snapshots ({snapshots.length})
        </button>
        <button className={tab === 'versions' ? 'active' : ''} onClick={() => setTab('versions')}>
          Versions ({versions.length})
        </button>
        <button
          type="button"
          className="rollback-help-trigger"
          onClick={() => setHelpOpen(true)}
          title="When should I use each of these?"
          aria-label="What's the difference?"
        >
          <svg
            className="rollback-help-icon"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span>What&rsquo;s the difference?</span>
        </button>
      </div>

      {loading && <div className="rollback-loading">Loading…</div>}

      {tab === 'changes' && (
        <div className="rollback-body">
          <div className="rollback-hint">
            Press <kbd>⌘Z</kbd> / <kbd>Ctrl+Z</kbd> to revert the most recent change.
          </div>
          {groupedChanges.length === 0 && !loading && (
            <div className="rollback-empty">No changes recorded yet.</div>
          )}
          {groupedChanges.map(({ key, items }) => {
            const head = items[0];
            const isBatch = items.length > 1 && head.changeBatchId;
            return (
              <div key={key} className={`rollback-change ${head.revertedAt ? 'reverted' : ''}`}>
                <div className="rollback-change-main">
                  <div className="rollback-change-summary">
                    {summarize(head)}
                    {isBatch && <span className="rollback-batch-count"> +{items.length - 1} related</span>}
                  </div>
                  <div className="rollback-change-meta">
                    {head.changedByUserName || `user ${head.changedByUserId}`} · {formatTimestamp(head.changedAt)}
                    {head.revertedAt && <span className="rollback-reverted-tag"> · reverted</span>}
                  </div>
                </div>
                {!head.revertedAt && (
                  <div className="rollback-change-actions">
                    <button onClick={() => handleRevert(head.id)}>Revert</button>
                    {isBatch && head.changeBatchId && (
                      <button onClick={() => handleRevertBatch(head.changeBatchId!)}>Revert batch</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'snapshots' && (
        <div className="rollback-body">
          <div className="rollback-toolbar">
            <button className="rollback-create" onClick={handleCreateSnapshot}>
              + Save snapshot now
            </button>
          </div>
          {snapshots.length === 0 && !loading && (
            <div className="rollback-empty">No snapshots yet.</div>
          )}
          {snapshots.map((s) => (
            <div key={s.id} className="rollback-snapshot">
              <div className="rollback-snapshot-main">
                <div className="rollback-snapshot-name">
                  {s.name || `Snapshot #${s.id}`}
                  <span className={`rollback-snapshot-type type-${s.snapshotType}`}> · {s.snapshotType}</span>
                </div>
                <div className="rollback-snapshot-meta">
                  {s.createdByUserName || `user ${s.createdByUserId}`} · {formatTimestamp(s.createdAt)}
                </div>
                {s.description && <div className="rollback-snapshot-desc">{s.description}</div>}
              </div>
              <div className="rollback-snapshot-actions">
                <button onClick={() => handleRestoreSnapshot(s.id)}>Restore</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {helpOpen && (
        <div className="modal-overlay" role="presentation" onClick={() => setHelpOpen(false)}>
          <div
            className="modal-content rollback-help-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rollback-help-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="rollback-help-title">Which rollback tool do I want?</h3>
              <button className="modal-close" onClick={() => setHelpOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="rollback-help-intro">
                All three tabs let you step back from a change. They differ in what they save,
                when they save it, and what you can do with it afterwards. Use the smallest tool
                that solves your problem.
              </p>
              <table className="rollback-help-table">
                <thead>
                  <tr>
                    <th>&nbsp;</th>
                    <th>Recent Changes</th>
                    <th>Snapshots</th>
                    <th>Versions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">What it captures</th>
                    <td>Each individual edit you make. One row per change.</td>
                    <td>The entire state of the timetable at a moment.</td>
                    <td>The entire state, numbered and labelled like a release.</td>
                  </tr>
                  <tr>
                    <th scope="row">Created automatically?</th>
                    <td>Yes. Every edit is logged.</td>
                    <td>Only before risky operations, such as restoring another snapshot.</td>
                    <td>Yes. Every time you publish to students.</td>
                  </tr>
                  <tr>
                    <th scope="row">Created manually?</th>
                    <td>No. This list reflects edits.</td>
                    <td>Yes, via the <em>Save snapshot now</em> button.</td>
                    <td>Yes, via the <em>Create version from current state</em> button.</td>
                  </tr>
                  <tr>
                    <th scope="row">Numbered?</th>
                    <td>By time.</td>
                    <td>By time.</td>
                    <td>v1, v2, v3 and so on, in order.</td>
                  </tr>
                  <tr>
                    <th scope="row">Compare two of them?</th>
                    <td>No.</td>
                    <td>No.</td>
                    <td><strong>Yes.</strong> Side-by-side coloured diff.</td>
                  </tr>
                  <tr>
                    <th scope="row">Roll back</th>
                    <td>Per-change Revert button, or one click to revert a whole batch.</td>
                    <td>Restore the entire timetable to that snapshot.</td>
                    <td>Restore the entire timetable to that version.</td>
                  </tr>
                  <tr>
                    <th scope="row">Best for</th>
                    <td>Reversing a specific edit you (or someone else) just made.</td>
                    <td>Personal save-point before trying something risky.</td>
                    <td>Official history of what students saw, and comparing publishes.</td>
                  </tr>
                </tbody>
              </table>

              <h4 className="rollback-help-heading">Rules of thumb</h4>
              <ul className="rollback-help-list">
                <li><strong>Just made a mistake.</strong> Press <kbd>&#8984;Z</kbd> or <kbd>Ctrl+Z</kbd>, or use the Revert button on that row in <em>Recent Changes</em>.</li>
                <li><strong>About to try a big change.</strong> Save a <em>Snapshot</em> first so you have a clean fall-back.</li>
                <li><strong>Want to see what changed between two publishes.</strong> Open <em>Versions</em>, pick <em>From</em> and <em>To</em>, then click Compare.</li>
                <li><strong>Need to roll the whole timetable back to a known good state.</strong> Restore the relevant Snapshot or Version. The current state is auto-saved first, so the restore itself is reversible.</li>
              </ul>

              <p className="rollback-help-footnote">
                Every rollback is itself recorded. If you revert by mistake, you can revert the revert.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-save" onClick={() => setHelpOpen(false)}>Got it</button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <ConfirmModal
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {promptDialog && (
        <PromptModal
          title={promptDialog.title}
          label={promptDialog.label}
          placeholder={promptDialog.placeholder}
          confirmText={promptDialog.confirmText}
          onSubmit={promptDialog.onSubmit}
          onCancel={() => setPromptDialog(null)}
        />
      )}

      {tab === 'versions' && (
        <div className="rollback-body">
          <div className="rollback-toolbar">
            <button className="rollback-create" onClick={handleCreateVersion}>
              + Create version from current state
            </button>
          </div>
          {versions.length === 0 && !loading && (
            <div className="rollback-empty">No versions yet. Publishing the timetable auto-creates one.</div>
          )}
          {versions.length >= 2 && (
            <div className="rollback-diff-controls">
              <label>
                From:{' '}
                <select value={diffFrom ?? ''} onChange={(e) => setDiffFrom(Number(e.target.value) || null)}>
                  {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.versionNumber} {v.label ? `– ${v.label}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                To:{' '}
                <select value={diffTo ?? ''} onChange={(e) => setDiffTo(Number(e.target.value) || null)}>
                  {versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.versionNumber} {v.label ? `– ${v.label}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <button onClick={handleRunDiff} disabled={!diffFrom || !diffTo || diffFrom === diffTo}>
                Compare
              </button>
            </div>
          )}
          {versions.map((v) => (
            <div key={v.id} className={`rollback-version ${v.isActive ? 'active' : ''}`}>
              <div className="rollback-version-main">
                <div className="rollback-version-name">
                  v{v.versionNumber}
                  {v.label && <span className="rollback-version-label"> — {v.label}</span>}
                  {v.isActive ? <span className="rollback-version-active"> · active</span> : null}
                </div>
                <div className="rollback-version-meta">
                  {v.createdByUserName || `user ${v.createdByUserId}`} · {formatTimestamp(v.createdAt)}
                </div>
                {v.description && <div className="rollback-version-desc">{v.description}</div>}
              </div>
              <div className="rollback-version-actions">
                {!v.isActive && (
                  <button onClick={() => handleRestoreVersion(v.id)}>Restore</button>
                )}
              </div>
            </div>
          ))}

          {diff && (
            <div className="rollback-diff">
              <h4>Differences</h4>
              {diff.templateChanged && (
                <div className="rollback-diff-section">
                  <div className="rollback-diff-section-title">Timetable metadata changed</div>
                  <ul>
                    {(diff.templateChangedFields || []).map((f) => (
                      <li key={f}>
                        <strong>{f}:</strong>{' '}
                        <span className="diff-before">{String(diff.templateBefore?.[f] ?? '∅')}</span>
                        {' → '}
                        <span className="diff-after">{String(diff.templateAfter?.[f] ?? '∅')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {diff.slotsAdded.length > 0 && (
                <div className="rollback-diff-section diff-added">
                  <div className="rollback-diff-section-title">Slots added ({diff.slotsAdded.length})</div>
                  <ul>
                    {diff.slotsAdded.map((s) => (
                      <li key={s.id}>
                        {s.date} {s.startTime}–{s.endTime} {s.name ? `· ${s.name}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {diff.slotsRemoved.length > 0 && (
                <div className="rollback-diff-section diff-removed">
                  <div className="rollback-diff-section-title">Slots removed ({diff.slotsRemoved.length})</div>
                  <ul>
                    {diff.slotsRemoved.map((s) => (
                      <li key={s.id}>
                        {s.date} {s.startTime}–{s.endTime} {s.name ? `· ${s.name}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {diff.slotsChanged.length > 0 && (
                <div className="rollback-diff-section diff-changed">
                  <div className="rollback-diff-section-title">Slots changed ({diff.slotsChanged.length})</div>
                  <ul>
                    {diff.slotsChanged.map((s) => (
                      <li key={s.id}>
                        <div>
                          {s.date} {s.startTime}–{s.endTime} {s.name ? `· ${s.name}` : ''}
                        </div>
                        {(s.changedFields || []).map((f) => (
                          <div key={f} className="diff-field">
                            <strong>{f}:</strong>{' '}
                            <span className="diff-before">{String(s.before?.[f] ?? '∅')}</span>
                            {' → '}
                            <span className="diff-after">{String(s.after?.[f] ?? '∅')}</span>
                          </div>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!diff.templateChanged &&
                diff.slotsAdded.length === 0 &&
                diff.slotsRemoved.length === 0 &&
                diff.slotsChanged.length === 0 && (
                  <div className="rollback-empty">Versions are identical.</div>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RollbackPanel;
